import React, {useMemo} from 'react'
import {Helmet} from 'react-helmet'
import {useLocation} from 'react-router-dom'
import {Box, Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {getAppDomain} from '../../../utils/utils'

import {SEARCH_PARAMS_LIMIT} from '../../../constants'

/**
 * PaginationMetaTags is a React component responsible for rendering meta tags related to pagination
 * for SEO optimization. It dynamically generates and injects <link> tags for "prev" and "next" pages
 * into the document head, based on the current page and total number of pages. This helps search engines
 * understand the structure of paginated content and improves the discoverability of subsequent or previous
 * pages in product listings.
 *
 * @component
 * @example
 * <PaginationMetaTags currentPage={2} totalPages={5} baseUrl="/products" />
 *
 * @param {Object} props - The component props.
 * @param {number} props.currentPage - The current page number in the pagination.
 * @param {number} props.totalPages - The total number of pages available.
 * @param {string} props.baseUrl - The base URL for the paginated product list.
 *
 * @returns {React.Element|null} Returns meta tags for pagination or null if not applicable.
 */
const PaginationMetaTags = ({total}) => {
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)

    // p=1 is treated as page 0 (first page)
    const rawPageParam = parseInt(urlParams.get('p'))
    const pageParam = isNaN(rawPageParam) || rawPageParam <= 1 ? 0 : rawPageParam - 1

    const canonicalParams = new URLSearchParams()
    if (rawPageParam && rawPageParam !== '1') {
        canonicalParams.set('p', rawPageParam)
    }

    const canonicalQuery = canonicalParams.toString()
    const baseUrlNoParams = `${getAppDomain()}${location.pathname}`
    const baseUrl = `${baseUrlNoParams}${canonicalQuery ? `?${canonicalQuery}` : ''}`
    const totalPages = Math.ceil(total / SEARCH_PARAMS_LIMIT)

    const prevPageUrl =
        pageParam > 1
            ? `${baseUrlNoParams}?p=${pageParam}`
            : pageParam === 1
            ? baseUrlNoParams
            : null

    const nextPageUrl = pageParam < totalPages - 1 ? `${baseUrlNoParams}?p=${pageParam + 2}` : null

    // Hidden pagination links for SEO
    const paginationLinks = useMemo(() => {
        return Array.from({length: totalPages}, (_, index) => {
            // page 1 must NOT exist → skip index 1
            if (index === 1) return null

            const pageParam = index === 0 ? '' : `?p=${index}`
            const url = `${baseUrl}${pageParam}`

            return (
                <Link key={index} href={url} display="block">
                    {url}
                </Link>
            )
        })
    }, [baseUrl, totalPages])

    return (
        <>
            <Helmet>
                {prevPageUrl && <link rel="prev" href={prevPageUrl} />}
                {nextPageUrl && <link rel="next" href={nextPageUrl} />}
                <link rel="canonical" href={baseUrl} />
            </Helmet>

            <Box display="none">{paginationLinks}</Box>
        </>
    )
}

PaginationMetaTags.propTypes = {
    total: PropTypes.number.isRequired
}

export default PaginationMetaTags
